// Comprehensive Domain Configuration Manager
// This file provides a unified interface for accessing all domain-specific content and settings

import { 
  DOMAIN_CONFIGS, 
  getDomainConfigByHostname, 
  getDomainConfigById,
  getAllDomainIds,
  getAllHostnames
} from './domains/index.js';
import { getDomainImageAuto } from '../utils/imageManager';

/**
 * Domain Configuration Manager
 * Provides a unified interface for accessing domain-specific content and settings
 */
export class DomainManager {
  constructor(domainId) {
    this.domainId = domainId;
    this.config = getDomainConfigById(domainId);
    
  }

  // Basic domain information
  get id() { return this.config?.id; }
  get name() { return this.config?.name; }
  get tagline() { return this.config?.tagline; }
  get description() { return this.config?.description; }
  
  // Assets
  get logo() { return this.config?.logo; }
  get whiteLogo() { return this.config?.whiteLogo; }
  get favicon() { return this.config?.favicon; }
  get icon() { return this.config?.icon; }
  
  // Navigation
  get pages() { return this.config?.pages || []; }
  get navigation() { return this.config?.navigation || []; }
  
  // CTAs
  get cta() { return this.config?.cta || {}; }
  
  // UI Configuration
  get variant() { return this.config?.variant || 'corporate'; }
  get showFeatures() { return this.config?.showFeatures ?? true; }
  get showTestimonials() { return this.config?.showTestimonials ?? true; }
  
  // SEO
  get seo() { return this.config?.seo || {}; }

  // Hero content with images
  getHeroContent() {
    if (!this.config?.hero) return null;
    
    return {
      ...this.config.hero,
      mainImage: getDomainImageAuto(this.domainId, 'hero-main', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']),
      secondaryImage: getDomainImageAuto(this.domainId, 'hero-secondary', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])
    };
  }

  // Features content with images
  getFeatures() {
    if (!this.config?.features) return null;
    
    return {
      ...this.config.features,
      items: this.config.features.items.map((item, index) => ({
        ...item,
        image: getDomainImageAuto(this.domainId, `feature-${index + 1}`, ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])
      }))
    };
  }

  // Solutions content with images
  getSolutions() {
    if (!this.config?.solutions) {
      return null;
    }
    
    const result = {
      ...this.config.solutions,
      sections: this.config.solutions.sections.map((section, index) => {
        const sectionWithImage = {
          ...section,
          image: getDomainImageAuto(this.domainId, `solution-${index + 1}`, ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])
        };
        return sectionWithImage;
      })
    };
    
    return result;
  }

  // Blog content with images
  getBlog() {
    if (!this.config?.blog) return null;
    
    const blogConfig = { ...this.config.blog };
    
    // Only add posts with images if posts array exists
    if (blogConfig.posts && Array.isArray(blogConfig.posts)) {
      blogConfig.posts = blogConfig.posts.map((post, index) => ({
        ...post,
        image: getDomainImageAuto(this.domainId, `feature-${index + 1}`, ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])
      }));
    }
    
    return blogConfig;
  }

  // Testimonials
  getTestimonials() {
    return this.config?.testimonials || [];
  }

  // Get any custom image for the domain
  getImage(imageName, extensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']) {
    return getDomainImageAuto(this.domainId, imageName, extensions);
  }

  // Get all available content types
  getContentTypes() {
    return {
      hero: this.getHeroContent(),
      features: this.getFeatures(),
      solutions: this.getSolutions(),
      blog: this.getBlog(),
      testimonials: this.getTestimonials()
    };
  }

  // Check if domain has specific content type
  hasContentType(type) {
    return !!this.config?.[type];
  }

  // Get domain-specific setting
  getSetting(key, defaultValue = null) {
    return this.config?.[key] ?? defaultValue;
  }

  // Get all domain settings
  getAllSettings() {
    return { ...this.config };
  }
}

// Factory function to create domain manager
export const createDomainManager = (domainId) => {
  return new DomainManager(domainId);
};

// Utility functions for backward compatibility
export const getDomainHeroContent = (domainId) => {
  const manager = createDomainManager(domainId);
  return manager.getHeroContent();
};

export const getDomainFeatures = (domainId) => {
  const manager = createDomainManager(domainId);
  return manager.getFeatures();
};

export const getDomainSolutions = (domainId) => {
  const manager = createDomainManager(domainId);
  return manager.getSolutions();
};

export const getDomainBlog = (domainId) => {
  const manager = createDomainManager(domainId);
  return manager.getBlog();
};

export const getDomainTestimonials = (domainId) => {
  const manager = createDomainManager(domainId);
  return manager.getTestimonials();
};

export const getDomainSEO = (domainId) => {
  const manager = createDomainManager(domainId);
  return manager.seo;
};

// Hook for React components
export const useDomainManager = (domainId) => {
  return createDomainManager(domainId);
};

// Export all domain configurations for direct access if needed
export { 
  DOMAIN_CONFIGS, 
  getDomainConfigByHostname, 
  getDomainConfigById,
  getAllDomainIds,
  getAllHostnames
};
